import { useState, useEffect } from "react";
import AddIcon from "@mui/icons-material/Add";
import TuneIcon from "@mui/icons-material/Tune";
import { Tooltip } from "@mui/material";
import { toast } from "react-toastify";
import getColumns from "./column.js";
import DynamicTable from "../../../../components/common/DynamicTable.jsx";
import useModal from "../../../../hooks/useModal.js";
import useAuthUser from "../../../../hooks/useAuthUser.js";
import useConfirm from "../../../../hooks/useConfirm.js";
import usePaginatedList from "../../../../hooks/usePaginatedList.js";
import AddEditLWFMaster from "./AddEditLWFMaster.jsx";
import LWFMasterFilter from "./LWFMasterFilter.jsx";
import { deleteLwfMaster, getLwfMasters, getZones } from "../../../../services/lwfMaster.js";

const AMOUNT_TYPE_OPTIONS = [
  { label: "Fixed", value: "fixed" },
  { label: "Percentage", value: "percentage" },
];

const FREQUENCY_OPTIONS = [
  { label: "Monthly", value: 1 },
  { label: "Quarterly", value: 3 },
  { label: "Half-Yearly", value: 6 },
  { label: "Yearly", value: 12 },
];

const LWFMasterList = () => {
  const { isAdminHrRole } = useAuthUser();
  const { confirm } = useConfirm();
  const { showModal } = useModal();
  const [showFilter, setShowFilter] = useState(false);
  const [zoneOptions, setZoneOptions] = useState([]);

  const [filterValues, setFilterValues] = useState({});
  const setValue = (key, val) => setFilterValues((prev) => ({ ...prev, [key]: val }));

  useEffect(() => {
    const fetchZones = async () => {
      try {
        const response = await getZones();
        const zones = response?.data?.data || response?.data || [];
        const formattedZones = zones.map((z) => ({ label: z._Name || z.name, value: z._ZoneID ?? z.id }));
        setZoneOptions(formattedZones);
      } catch (err) {
        console.error("Failed to load zones for filter", err);
      }
    };
    fetchZones();
  }, []);

  const [fields, setFields] = useState([]);

  useEffect(() => {
    setFields([
      { key: "state_id", label: "State", type: "select", options: zoneOptions },
      { key: "amount_type", label: "Amount Type", type: "select", options: AMOUNT_TYPE_OPTIONS },
      { key: "frequency", label: "Frequency", type: "select", options: FREQUENCY_OPTIONS },
      { key: "salary_from", label: "Salary From", type: "text" },
      { key: "salary_to", label: "Salary To", type: "text" },
    ]);
  }, [zoneOptions]);

  const fetchData = async (params) => {
    return await getLwfMasters(params?.page || 1, params);
  };

  const { data, pagination, setPage, setItemsPerPage, setFilters, refresh } = usePaginatedList(fetchData);

  const handleApplyFilters = () => {
    setFilters({ filters: filterValues });
    setShowFilter(false);
  };

  const handleClearFilters = () => {
    setFilterValues({});
    setFilters({ filters: {} });
  };

  const handleCreateLwfMaster = () => {
    showModal("Add LWF", <AddEditLWFMaster refresh={refresh} />);
  };

  const handleEditLwfMaster = (existingField) => {
    showModal("Edit LWF", <AddEditLWFMaster refresh={refresh} existingField={existingField} />);
  };

  const handleDelete = async (id) => {
    try {
      const isConfirmed = await confirm({
        title: "Confirm Deletion",
        content: "Are you sure you want to delete this LWF record?",
        confirmText: "Yes, Delete",
        cancelText: "Cancel",
      });
      if (!isConfirmed) return;

      const response = await deleteLwfMaster(id);
      toast.success(response?.data?.message || "LWF deleted successfully!");
      refresh();
    } catch (error) {
      console.log("Error in deleting LWF", error);
      toast.error(error?.response?.data?.message || "Error in deleting LWF");
    }
  };

  if (!isAdminHrRole) return null;

  return (
    <>
      <div className="bg-light mb-2">
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              <label className="mb-0 text-muted small">Show</label>
              <select
                className="form-select form-select-sm"
                style={{ width: "80px" }}
                value={pagination?.itemsPerPage || 20}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-muted small">entries</span>
            </div>

            <div className="d-flex gap-2 flex-wrap">
              <button className="btn btn-success btn-sm d-flex align-items-center" onClick={handleCreateLwfMaster}>
                <AddIcon sx={{ fontSize: 18 }} className="me-1" />
                <span className="d-none d-sm-inline">Add LWF</span>
              </button>

              <Tooltip title="Filter">
                <button className="btn btn-outline-dark btn-sm" onClick={() => setShowFilter(!showFilter)}>
                  <TuneIcon sx={{ fontSize: 18 }} />
                </button>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>

      <LWFMasterFilter
        fields={fields}
        isOpen={showFilter}
        onClose={() => setShowFilter(false)}
        values={filterValues}
        setValue={setValue}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
      />

      <DynamicTable
        columns={getColumns(handleEditLwfMaster, handleDelete)}
        data={data}
        rowKey="id"
        currentPage={pagination?.currentPage}
        totalPages={pagination?.totalPages}
        itemsPerPage={pagination?.itemsPerPage}
        totalItems={pagination?.totalItems}
        onPageChange={(page) => setPage(page)}
      />
    </>
  );
};

export default LWFMasterList;
